import React, { useEffect, useState, useMemo } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Checkbox } from "../ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { MapPin, Loader2, CheckCircle2, AlertTriangle, RotateCw, Info, Home, Warehouse, Umbrella, HelpCircle, X } from "lucide-react";
import { autoMeasureLookup, autoMeasureResolveAddress } from "../../lib/api";
import { canonicalizeAddress } from "../../lib/addressCanonical";
import { toast } from "sonner";
// v14at-p4 F4 — Optional async-flow hook. Alleen actief wanneer feature-flag
// REACT_APP_AUTOMEASURE_ASYNC_ENABLED=true. Bij flag=false retourneert hij
// `asyncEnabled=false` en wordt elke call doorverwezen naar de sync-flow.
import { useZoneMeasurementJob } from "../../hooks/useZoneMeasurementJob";
import { ReviewMeasurementDialog } from "../measurement-jobs/ReviewMeasurementDialog";

// v14ao — AutoMeasure™ · Zone-level building block.
//
//   Woning
//   ↓
//   AutoMeasure™ zone-block (dit component)
//   ↓
//   GEVELVLAKKEN (bestaande facade_surfaces)
//
// States:
//   pending   — geen meting gedaan; toon "Te meten gebouw: <resolved address>"
//   loading   — meting bezig
//   success   — compact status "✓ Gemeten met AutoMeasure™ · N gevels"
//   confirm   — zone had al > 1 surface: 3-way (Behoud+toevoegen / Vervangen / Annuleren)
//
// De component muteert zelf niet de zone. Alle wijzigingen gaan via callbacks:
//   onMeasured(building, mode, strategy?)  — na succesvolle meting; parent past toe
//   onAddressChange(newAddr)               — na "Adres wijzigen"; parent slaat op

const CARDINAL_NAMES_LONG = { N: "Noordzijde", O: "Oostzijde", Z: "Zuidzijde", W: "Westzijde" };

// v14ar — Bepaal hoeveel PaintMeasure-gevelvlakken er ontstaan voor een building.
// Prioriteit: components[].physical_facades (variabel N per geometrie); als er
// geen componenten zijn (legacy meting), val terug op views[].length.
const countPaintableSurfaces = (building, selectedIds) => {
  if (!building) return 0;
  const comps = Array.isArray(building.components) ? building.components : [];
  if (comps.length) {
    const wanted = selectedIds
      ? new Set(selectedIds)
      : new Set(comps.filter((c) => c.include_by_default).map((c) => c.component_id));
    let n = 0;
    for (const c of comps) {
      if (!wanted.has(c.component_id)) continue;
      for (const f of c.physical_facades || []) {
        // v14at-p3b — Alleen echt-schilderbare, niet-gedeelde segmenten
        // worden automatisch tot gevelvlak omgezet (zie PaintMeasure).
        if (!f.is_shared && f.is_paintable_default) n += 1;
      }
    }
    return n;
  }
  return (building.views || []).length;
};

const COMPONENT_ICON = {
  main: Home,
  main_by_address_anchor: Home,
  main_virtual_facade_aggregate: Home,
  attached_building: Warehouse,
  adjacent_building: Warehouse,
  secondary_detached: Warehouse,
  canopy: Umbrella,
};

export default function AutoMeasureZoneBlock({
  zone,                          // WorkZone (exterior_area)
  projectId = null,              // v14at-p4 F4.3 — expliciete project-context van PaintMeasure
  defaultAddress,                // project.worksite_address || client.address
  automeasureEnabled,            // company.automeasure_enabled
  moduleSupported = true,        // supports_automeasure (paintmeasure = true)
  hasExistingSurfaces = false,   // (zone.facade_surfaces || []).length > 1  (target-anchor niet meegerekend)
  onMeasured,                    // async (building, strategy) => void       (legacy sync pad)
  onApplyServerAuthored,         // v14at-p5 — async (applyResponse) => void (nieuwe atomic pad)
  onDetailsOpen,                 // () => void
}) {
  const stored = zone?.automeasure_building || null;
  // v14ao-fix — Canonicaliseer alle inbound adres-strings (verwijdert \n van
  // multi-line client/project adressen). Meting-adres uit automeasure_building
  // is doorgaans al canonical (van address_resolved), maar we blijven defensief.
  const canonicalStored = stored?.measurement_address ? canonicalizeAddress(stored.measurement_address) : "";
  const canonicalDefault = canonicalizeAddress(defaultAddress || "");
  const [address, setAddress] = useState(canonicalStored || canonicalDefault);
  const [resolved, setResolved] = useState(canonicalStored ? { address: canonicalStored, x: null, y: null, score: null } : null);
  const [resolving, setResolving] = useState(false);
  const [measuring, setMeasuring] = useState(false);
  const [pendingBuilding, setPendingBuilding] = useState(null);   // wanneer confirm nodig
  const [confirmMode, setConfirmMode] = useState(null);           // "prompt" | "components" | null
  const [componentPicker, setComponentPicker] = useState(null);   // v14ar — {building, selected:Set}
  const [warnDifferent, setWarnDifferent] = useState(null);       // {oldId, newId} wanneer building_id verandert
  const [addressDialogOpen, setAddressDialogOpen] = useState(false);
  const [addressDraft, setAddressDraft] = useState("");
  // v14at-p3f — Service-unavailable state (Vlaamse GBG down). Persistent
  // banner in het AutoMeasure-blok, blijft staan tot user × klikt. Vervangt
  // p3e's `duration: Infinity` toast omdat sonner v2 dat in de deployed
  // preview niet betrouwbaar aanhoudt (toast dismount na re-render).
  const [serviceUnavailable, setServiceUnavailable] = useState(null);   // {title, message} | null

  // v14at-p4 F4 — Async-flow hook. Als flag=false retourneert `asyncEnabled=false`.
  // v14at-p4 F4.3 (Defect E) — projectId komt uit PaintMeasure-context via prop
  // (project.id). zone.project_id bestaat niet op zone-subdocumenten in
  // quote.work_zones; die fallback is enkel defensief voor theoretische
  // future refactors.
  const asyncJob = useZoneMeasurementJob({
    projectId: projectId || zone?.project_id || null,
    zoneId: zone?.id || null,
    address: canonicalizeAddress(resolved?.address || address || ""),
  });
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);

  // Bij eerste render OR wanneer we nog geen resolved hebben én er is een address → resolve.
  useEffect(() => {
    if (!automeasureEnabled || !moduleSupported) return;
    if (stored) return; // meting-adres al vast
    if (!address?.trim()) { setResolved(null); return; }
    let cancelled = false;
    (async () => {
      setResolving(true);
      try {
        const canonical = canonicalizeAddress(address.trim());
        const r = await autoMeasureResolveAddress(canonical);
        if (cancelled) return;
        setResolved(r?.resolved ? { address: canonicalizeAddress(r.address_resolved), x: r.x, y: r.y, score: r.match_confidence } : null);
      } catch (e) {
        if (!cancelled) setResolved(null);
      } finally {
        if (!cancelled) setResolving(false);
      }
    })();
    return () => { cancelled = true; };
  }, [address, stored, automeasureEnabled, moduleSupported]);

  // v14ao — Feature-gate NA hooks (React Hooks rules).
  if (!automeasureEnabled || !moduleSupported) return null;

  const hasResolvedAddress = !!(resolved?.address);
  // v14at-p4 F4.2 (Defect D) — In async-modus is de background worker de
  // officiële bron voor address resolution. De knop mag dus niet
  // uitsluitend disabled worden omdat de sync preflight resolver faalt
  // (bv. tijdens een korte GBG-outage). We vereisen enkel een niet-lege
  // measurement-input; de worker produceert daarna een duidelijke
  // async failed-status indien het adres werkelijk ongeldig is.
  // Sync-flow behoudt het bestaande gedrag (vereist hasResolvedAddress).
  const asyncReady = !!(asyncJob && asyncJob.asyncEnabled && !asyncJob.jobId);
  const hasNonEmptyAddress = !!canonicalizeAddress(
    (resolved?.address || address || "")
  ).trim();
  const canStartMeasurement = asyncReady
    ? hasNonEmptyAddress
    : hasResolvedAddress;

  const runMeasurement = async () => {
    // v14at-p4 F4.2 (Defect D) — Guard splitst async vs sync. In async
    // mogen we doorgaan zodra het adres niet-leeg is; worker beslist.
    if (asyncReady) {
      if (!hasNonEmptyAddress) {
        toast.error("Vul eerst een adres in.");
        return;
      }
      try {
        await asyncJob.start();
        // NotificationCenter + zone-badge nemen over.
        return;
      } catch (e) {
        const s = e?.response?.status;
        if (s !== 404 && e?.message !== "async_disabled") {
          toast.error("AutoMeasure async kon niet gestart worden — val terug op sync-modus.");
        }
        // Fall through naar sync-flow (die vereist hasResolvedAddress).
      }
    }
    // Sync-flow — ongewijzigd gedrag.
    if (!hasResolvedAddress) { toast.error("Adres kon niet gevonden worden."); return; }
    setMeasuring(true);
    setServiceUnavailable(null);   // v14at-p3f — vorige banner wissen bij nieuwe poging
    try {
      // v14ao-fix — Stuur canonical resolved (of canonicalized address) naar lookup,
      // nooit raw multi-line strings.
      const measureAddress = canonicalizeAddress(resolved.address || address);
      const r = await autoMeasureLookup(measureAddress);
      if ((r.errors || []).length) {
        toast.error(`AutoMeasure: ${r.errors.join(", ")}`);
        return;
      }
      // v14ao-fix — Persist canonical address_resolved (backend geeft dit al terug).
      r.address_resolved = canonicalizeAddress(r.address_resolved || measureAddress);
      // Building-id vergelijking: als er al een meting was met een ander building_id → warn.
      if (stored?.building_id && r.building_id && stored.building_id !== r.building_id) {
        setWarnDifferent({ oldId: stored.building_id, newId: r.building_id, building: r });
        return;
      }
      // v14ar — Toon een component-picker wanneer er >1 zinvolle bouwdelen zijn
      // (bv. hoofdgebouw + aanbouw + afdak). Simpele gebouwen (1 component)
      // gaan door zonder extra klik (§17). De picker respecteert §8: user
      // beslist scope; wij classificeren enkel.
      const meaningfulComps = (r.components || []).filter((c) => (c.physical_facades || []).some((f) => !f.is_shared));
      if (meaningfulComps.length > 1) {
        setComponentPicker({
          building: r,
          selected: new Set(meaningfulComps.filter((c) => c.include_by_default).map((c) => c.component_id)),
          strategy: hasExistingSurfaces ? null : "keep",  // null = ask after selection
        });
        return;
      }
      if (hasExistingSurfaces) {
        setPendingBuilding(r);
        setConfirmMode("prompt");
        return;
      }
      await onMeasured(r, "keep");
      // v14ar — Aantal gevelvlakken = werkelijk gemaakte fysieke gevels
      // (variabel per geometrie), NIET het aantal kardinale views.
      const nSurfaces = countPaintableSurfaces(r, null);
      toast.success(`Gemeten met AutoMeasure™ — ${nSurfaces} gevelvlak${nSurfaces === 1 ? "" : "ken"} aangemaakt.`);
    } catch (e) {
      const s = e?.response?.status;
      // v14at-p3d — 503 met gestructureerd detail {code,title,message}
      const d = e?.response?.data?.detail;
      const detailStr = (d && typeof d === "object")
        ? `${d.title || "AutoMeasure fout"}${d.message ? " — " + d.message : ""}`
        : (d || e.message || "AutoMeasure fout");
      // v14at-p3f — Service-unavailable (Vlaamse GBG down) toont een
      // persistente state-based banner (component-lokaal) in plaats van
      // een auto-dismiss toast. Blijft staan tot user × klikt of tot een
      // volgende succesvolle poging plaatsvindt.
      const isServiceUnavailable = s === 503 && d && typeof d === "object"
        && d.code === "flanders_gbg_unavailable";
      if (isServiceUnavailable) {
        setServiceUnavailable({
          title: d.title || "AutoMeasure is tijdelijk niet beschikbaar",
          message: d.message || "",
        });
      } else if (s === 403) {
        toast.error("AutoMeasure niet ingeschakeld — check Instellingen.");
      } else {
        toast.error(detailStr);
      }
    } finally {
      setMeasuring(false);
    }
  };

  const confirmWithStrategy = async (strategy) => {
    if (!pendingBuilding) return;
    setConfirmMode(null);
    try {
      await onMeasured(pendingBuilding, strategy);
      const n = countPaintableSurfaces(pendingBuilding, null);
      toast.success(strategy === "replace"
        ? `Gevelvlakken vervangen — ${n} nieuw${n === 1 ? "" : "e"} gevelvlak${n === 1 ? "" : "ken"}.`
        : `Gevelvlakken toegevoegd — ${n} extra gevelvlak${n === 1 ? "" : "ken"}.`);
    } finally {
      setPendingBuilding(null);
    }
  };

  // v14ar — Component-picker toepassen. Roept `onMeasured` met een 4e argument
  // (`selectedComponentIds`) zodat PaintMeasure.applyMeasurementToZone alleen
  // de gekozen componenten omzet in gevelvlakken.
  const applyComponentPicker = async (strategy) => {
    if (!componentPicker) return;
    const { building, selected } = componentPicker;
    const ids = Array.from(selected);
    setComponentPicker(null);
    try {
      await onMeasured(building, strategy, ids);
      const n = countPaintableSurfaces(building, ids);
      toast.success(`Gemeten met AutoMeasure™ — ${n} gevelvlak${n === 1 ? "" : "ken"} aangemaakt.`);
    } catch (e) {
      toast.error(e?.message || "Toepassen mislukt");
    }
  };

  const toggleComponentInPicker = (compId) => {
    setComponentPicker((prev) => {
      if (!prev) return prev;
      const next = new Set(prev.selected);
      if (next.has(compId)) next.delete(compId);
      else next.add(compId);
      return { ...prev, selected: next };
    });
  };

  const confirmDifferentBuilding = async () => {
    if (!warnDifferent) return;
    const { building } = warnDifferent;
    setWarnDifferent(null);
    if (hasExistingSurfaces) {
      setPendingBuilding(building);
      setConfirmMode("prompt");
    } else {
      await onMeasured(building, "replace");
      toast.success("Gebouw vervangen — nieuwe meting toegepast.");
    }
  };

  const submitAddressChange = () => {
    const v = canonicalizeAddress(addressDraft.trim());
    if (!v) { toast.error("Vul een adres in."); return; }
    setAddress(v);
    setResolved(null);
    setAddressDialogOpen(false);
  };

  // --- Render ---
  if (stored) {
    // SUCCESS state (compact). v14ar — n_facades reflecteert werkelijk
    // gemaakte physical_facades, niet meer 4 (of het aantal views).
    const nGevels = stored.n_facades ?? "?";
    const nLabel = `${nGevels} gevelvlak${nGevels === 1 ? "" : "ken"}`;
    return (
      <div className="border-2 border-[#6B8E6B]/40 bg-[#F2F5EE]/50 rounded-lg px-3 py-2.5 flex items-center gap-3 shadow-sm" data-testid={`automeasure-zone-block-${zone.id}`}>
        <CheckCircle2 className="w-4 h-4 text-[#5D7259] shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="text-[13px] font-semibold text-[#2C2C2A]">
            Gemeten met AutoMeasure™ · {nLabel}
          </div>
          <div className="text-[11px] text-[#5D7259] truncate">{stored.measurement_address}</div>
        </div>
        <button
          type="button"
          onClick={() => { setAddressDraft(canonicalizeAddress(stored.measurement_address || "")); setAddressDialogOpen(true); }}
          className="text-[11px] text-[#5D7259] hover:text-[#2C2C2A]"
          data-testid={`automeasure-zone-remeasure-${zone.id}`}
          title="Opnieuw meten met ander adres"
        >
          <RotateCw className="w-3.5 h-3.5" />
        </button>
        <button
          type="button"
          onClick={onDetailsOpen}
          className="text-[11px] text-[#D96C4A] hover:underline"
          data-testid={`automeasure-zone-details-${zone.id}`}
        >
          Details
        </button>

        {/* Address change dialog voor "Opnieuw meten". */}
        {addressDialogOpen && (
          <AddressChangeDialog
            initial={addressDraft}
            onCancel={() => setAddressDialogOpen(false)}
            onSubmit={(v) => { setAddress(canonicalizeAddress(v)); setResolved(null); setAddressDialogOpen(false); toast.info("Klik opnieuw op 'Gebouw opmeten'."); }}
            onSubmitRemeasure={async (v) => {
              const cv = canonicalizeAddress(v);
              setAddress(cv); setResolved(null); setAddressDialogOpen(false);
              setResolving(true);
              try {
                const r = await autoMeasureResolveAddress(cv);
                setResolved(r?.resolved ? { address: canonicalizeAddress(r.address_resolved), x: r.x, y: r.y, score: r.match_confidence } : null);
              } finally { setResolving(false); }
            }}
          />
        )}
      </div>
    );
  }

  // PENDING state.
  return (
    <div className="border-2 border-dashed border-[#D96C4A]/50 bg-[#FFF5EF]/60 rounded-lg p-3 space-y-2" data-testid={`automeasure-zone-block-${zone.id}`}>
      {/* v14at-p4 F4 — Async-job banners (alleen zichtbaar bij actieve job) */}
      {asyncJob.jobId && asyncJob.uiState === "pending" && (
        <div className="rounded-md border border-[#6B8E6B]/50 bg-[#F2F5EE] p-3 flex items-start gap-2" data-testid={`automeasure-zone-async-pending-${zone.id}`}>
          <Loader2 className="w-4 h-4 text-[#5D7259] animate-spin mt-0.5" />
          <div className="flex-1">
            <div className="text-[13px] font-semibold text-[#2C2C2A]">AutoMeasure is bezig met de opmeting</div>
            <div className="text-[11px] text-[#5D7259] mt-0.5">
              We halen de beschikbare gebouwgegevens op bij de officiële databronnen. Dit kan afhankelijk van de regio even duren. Je kunt ondertussen gewoon verderwerken in EmmaMeasure.
            </div>
            {asyncJob.job?.progress_hint && (
              <div className="text-[11px] text-[#3F5E3F] mt-1">Status: {asyncJob.job.progress_hint}</div>
            )}
          </div>
          <Button
            size="sm" variant="ghost"
            onClick={() => asyncJob.cancel()}
            className="text-[11px] h-7 px-2 text-[#5D7259]"
            data-testid={`automeasure-zone-async-cancel-${zone.id}`}
          >× Annuleren</Button>
        </div>
      )}
      {asyncJob.jobId && asyncJob.uiState === "completed" && (
        <div className="rounded-md border border-[#6B8E6B]/60 bg-[#F2F5EE] p-3 flex items-start gap-2" data-testid={`automeasure-zone-async-completed-${zone.id}`}>
          <CheckCircle2 className="w-4 h-4 text-[#6B8E6B] mt-0.5" />
          <div className="flex-1">
            <div className="text-[13px] font-semibold text-[#2C2C2A]">✓ AutoMeasure is klaar</div>
            <div className="text-[11px] text-[#5D7259] mt-0.5">Je meting is beschikbaar. Bekijk ze om de gevelvlakken aan te maken.</div>
          </div>
          <Button
            size="sm"
            onClick={() => { asyncJob.review(); setReviewDialogOpen(true); }}
            className="bg-[#6B8E6B] hover:bg-[#5B7D5B] text-white text-xs h-8"
            data-testid={`automeasure-zone-async-review-${zone.id}`}
          >▶ Bekijk meting</Button>
          <Button
            size="sm" variant="ghost"
            onClick={() => asyncJob.dismiss()}
            className="text-[11px] h-7 px-2 text-[#5D7259]"
          >×</Button>
        </div>
      )}
      {asyncJob.jobId && asyncJob.uiState === "failed" && (
        <div className="rounded-md border border-[#B84B45]/60 bg-[#FDF0EE] p-3 flex items-start gap-2" data-testid={`automeasure-zone-async-failed-${zone.id}`}>
          <AlertTriangle className="w-4 h-4 text-[#B84B45] mt-0.5" />
          <div className="flex-1">
            <div className="text-[13px] font-semibold text-[#B84B45]">⚠ {asyncJob.job?.error_title || "AutoMeasure niet gelukt"}</div>
            <div className="text-[11px] text-[#2C2C2A] mt-0.5">{asyncJob.job?.error_message || "Probeer opnieuw of val terug op manuele invoer."}</div>
          </div>
          <Button
            size="sm" variant="outline"
            onClick={async () => {
              // v14at-p4 F4.4 (Defect H) — Canonieke retry via dezelfde
              // start()-flow. GEEN lokale UI-fake: backend bepaalt of dit
              // een nieuwe (201) of deduplicated (200) job wordt.
              if (!hasNonEmptyAddress) {
                toast.error("Vul eerst een adres in.");
                return;
              }
              try {
                await asyncJob.retry();
              } catch (e) {
                const s = e?.response?.status;
                if (s !== 404 && e?.message !== "async_disabled") {
                  toast.error("Opnieuw proberen mislukte — probeer later opnieuw.");
                }
              }
            }}
            className="text-[11px] h-7 px-2"
            data-testid={`automeasure-zone-async-retry-${zone.id}`}
          >↻ Opnieuw</Button>
          <Button
            size="sm" variant="ghost"
            onClick={() => asyncJob.dismiss()}
            className="text-[11px] h-7 px-2 text-[#5D7259]"
          >×</Button>
        </div>
      )}
      {asyncJob.jobId && asyncJob.uiState === "cancelled" && asyncJob.isSuperseded && (
        <div className="rounded-md border border-[#B48A33]/60 bg-[#FDF8EB] p-3 flex items-start gap-2" data-testid={`automeasure-zone-async-superseded-${zone.id}`}>
          <Info className="w-4 h-4 text-[#B48A33] mt-0.5" />
          <div className="flex-1">
            <div className="text-[13px] font-semibold text-[#7A5A1A]">Deze meting is niet meer bruikbaar</div>
            <div className="text-[11px] text-[#2C2C2A] mt-0.5">Het zone-adres is ondertussen gewijzigd. Start een nieuwe meting om verder te gaan.</div>
          </div>
          <Button size="sm" variant="ghost" onClick={() => asyncJob.dismiss()} className="text-[11px] h-7 px-2 text-[#5D7259]">×</Button>
        </div>
      )}
      {/* ReviewDialog voor async-flow */}
      {asyncJob.jobId && (asyncJob.uiState === "reviewed" || asyncJob.uiState === "completed" || asyncJob.uiState === "applying") && (
        <ReviewMeasurementDialog
          open={reviewDialogOpen}
          onOpenChange={setReviewDialogOpen}
          job={asyncJob.job}
          hasExistingSurfaces={hasExistingSurfaces}
          onApply={asyncJob.apply}
          onApplyServerAuthored={onApplyServerAuthored}
          onMeasuredFallback={onMeasured}
        />
      )}
      <div className="flex items-center gap-2">
        <MapPin className="w-4 h-4 text-[#D96C4A]" />
        <div className="text-[13px] font-bold uppercase tracking-wider text-[#D96C4A]">AutoMeasure™</div>
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-wider font-bold text-[#5D7259]">Te meten gebouw</div>
        <div className="mt-0.5 flex items-center gap-2 min-h-[24px]">
          {resolving ? (
            <div className="flex items-center gap-1.5 text-[13px] text-[#A8A7A0]"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Adres opzoeken…</div>
          ) : hasResolvedAddress ? (
            <>
              <div className="text-[13px] font-medium text-[#2C2C2A] flex-1 min-w-0 truncate" data-testid={`automeasure-zone-resolved-${zone.id}`}>
                {resolved.address}
              </div>
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#F2F5EE] text-[#3F5E3F] border border-[#6B8E6B]/40 font-semibold shrink-0" data-testid={`automeasure-zone-check-${zone.id}`}>
                ✓ Adres gevonden
              </span>
            </>
          ) : address ? (
            <div className="text-[13px] text-[#B84B45] flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5" /> Adres niet gevonden</div>
          ) : (
            <div className="text-[13px] text-[#A8A7A0] italic">Nog geen adres ingevuld</div>
          )}
        </div>
      </div>
      <div className="flex flex-wrap gap-2 pt-1">
        <Button
          onClick={runMeasurement}
          disabled={!canStartMeasurement || measuring}
          className="bg-[#D96C4A] hover:bg-[#C05A3C] text-white h-9"
          data-testid={`automeasure-zone-measure-${zone.id}`}
        >
          {measuring ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Gebouw opmeten…</> : "Gebouw opmeten"}
        </Button>
        <Button
          onClick={() => { setAddressDraft(canonicalizeAddress(address || "")); setAddressDialogOpen(true); }}
          variant="outline"
          className="h-9 border-[#D96C4A] text-[#D96C4A] hover:bg-[#FFF5EF]"
          data-testid={`automeasure-zone-change-address-${zone.id}`}
        >
          Adres wijzigen
        </Button>
      </div>

      {/* v14at-p3f — Persistente service-unavailable banner (blijft staan
          tot user × klikt of tot volgende succesvolle poging). Vervangt
          sonner-toast om betrouwbaar persistent gedrag te garanderen. */}
      {serviceUnavailable && (
        <div
          role="alert"
          className="mt-2 rounded-lg bg-[#FFF5EF] border-2 border-[#B84B45] p-3 flex items-start gap-2.5"
          data-testid={`automeasure-service-unavailable-${zone.id}`}
        >
          <AlertTriangle className="w-4 h-4 text-[#B84B45] mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-[13px] font-semibold text-[#B84B45]">
              {serviceUnavailable.title}
            </div>
            {serviceUnavailable.message && (
              <div className="text-[12px] text-[#2C2C2A] mt-0.5 leading-snug">
                {serviceUnavailable.message}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={() => setServiceUnavailable(null)}
            aria-label="Melding sluiten"
            className="text-[#B84B45] hover:text-[#8B3835] shrink-0 p-0.5 rounded hover:bg-[#B84B45]/10 transition"
            data-testid={`automeasure-service-unavailable-close-${zone.id}`}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Confirm-flow bij bestaande surfaces */}
      {confirmMode === "prompt" && pendingBuilding && (
        <div className="mt-2 rounded-lg bg-white border border-[#D4A373] p-3 space-y-2" data-testid={`automeasure-zone-confirm-${zone.id}`}>
          <div className="text-[13px] font-semibold text-[#8B4A2E]">Bestaande gevelvlakken gedetecteerd</div>
          <div className="text-[12px] text-[#4F4F4D]">De zone bevat al gevelvlakken. Wat wil je doen?</div>
          <div className="flex flex-col sm:flex-row gap-2 pt-1">
            <Button onClick={() => confirmWithStrategy("keep")} className="bg-[#6B8E6B] hover:bg-[#5B7D5B] text-white text-xs h-9" data-testid={`automeasure-zone-confirm-keep-${zone.id}`}>Bestaande behouden + AutoMeasure toevoegen</Button>
            <Button onClick={() => confirmWithStrategy("replace")} variant="outline" className="border-[#B84B45] text-[#B84B45] hover:bg-[#FDF0EE] text-xs h-9" data-testid={`automeasure-zone-confirm-replace-${zone.id}`}>Bestaande vervangen door AutoMeasure</Button>
            <Button onClick={() => { setConfirmMode(null); setPendingBuilding(null); }} variant="ghost" className="text-xs h-9" data-testid={`automeasure-zone-confirm-cancel-${zone.id}`}>Annuleren</Button>
          </div>
        </div>
      )}

      {/* v14ar — Component-picker: alleen tonen wanneer >1 zinvol bouwdeel */}
      {componentPicker && (
        <div className="mt-2 rounded-lg bg-white border border-[#6B8E6B]/50 p-3 space-y-3" data-testid={`automeasure-zone-component-picker-${zone.id}`}>
          <div>
            <div className="text-[13px] font-semibold text-[#2C2C2A]">
              {(componentPicker.building.components || []).filter((c) => (c.physical_facades || []).some((f) => !f.is_shared)).length} bouwdelen herkend
            </div>
            <div className="text-[11px] text-[#5D7259] mt-0.5">
              Kies welke bouwdelen je in de meting wil opnemen. Je kunt dit later altijd manueel aanpassen.
            </div>
          </div>
          <div className="space-y-1.5">
            {(componentPicker.building.components || []).map((comp) => {
              const nSurfaces = (comp.physical_facades || []).filter((f) => !f.is_shared && f.is_paintable_default).length;
              if (nSurfaces === 0) return null;
              const Icon = COMPONENT_ICON[comp.role] || HelpCircle;
              const isChecked = componentPicker.selected.has(comp.component_id);
              return (
                <label
                  key={comp.component_id}
                  className={`flex items-center gap-2.5 px-2.5 py-2 rounded border cursor-pointer transition ${
                    isChecked ? "bg-[#F2F5EE] border-[#6B8E6B]/60" : "bg-white border-[#E5E5E1] hover:bg-[#FBFBF8]"
                  }`}
                  data-testid={`automeasure-zone-component-${comp.component_id}-${zone.id}`}
                >
                  <Checkbox
                    checked={isChecked}
                    onCheckedChange={() => toggleComponentInPicker(comp.component_id)}
                    data-testid={`automeasure-zone-component-check-${comp.component_id}-${zone.id}`}
                  />
                  <Icon className="w-4 h-4 text-[#5D7259] shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-[12.5px] font-medium text-[#2C2C2A]">
                      {comp.label}
                    </div>
                    <div className="text-[10.5px] text-[#5D7259]">
                      {nSurfaces} gevelvlak{nSurfaces === 1 ? "" : "ken"}
                      {comp.footprint_area_m2 ? ` · ${comp.footprint_area_m2.toFixed(1)} m² grondopp.` : ""}
                      {comp.classification_confidence === "low" || comp.classification_confidence === "uncertain"
                        ? " · onzeker"
                        : ""}
                    </div>
                  </div>
                </label>
              );
            })}
          </div>
          <div className="flex flex-wrap gap-2 pt-1">
            {hasExistingSurfaces ? (
              <>
                <Button
                  onClick={() => applyComponentPicker("keep")}
                  className="bg-[#6B8E6B] hover:bg-[#5B7D5B] text-white text-xs h-9"
                  disabled={componentPicker.selected.size === 0}
                  data-testid={`automeasure-zone-component-apply-keep-${zone.id}`}
                >
                  Toevoegen aan bestaande
                </Button>
                <Button
                  onClick={() => applyComponentPicker("replace")}
                  variant="outline"
                  className="border-[#B84B45] text-[#B84B45] hover:bg-[#FDF0EE] text-xs h-9"
                  disabled={componentPicker.selected.size === 0}
                  data-testid={`automeasure-zone-component-apply-replace-${zone.id}`}
                >
                  Vervang bestaande
                </Button>
              </>
            ) : (
              <Button
                onClick={() => applyComponentPicker("keep")}
                className="bg-[#D96C4A] hover:bg-[#C05A3C] text-white text-xs h-9"
                disabled={componentPicker.selected.size === 0}
                data-testid={`automeasure-zone-component-apply-${zone.id}`}
              >
                Toepassen
              </Button>
            )}
            <Button
              onClick={() => setComponentPicker(null)}
              variant="ghost"
              className="text-xs h-9"
              data-testid={`automeasure-zone-component-cancel-${zone.id}`}
            >
              Annuleren
            </Button>
          </div>
        </div>
      )}

      {/* Warn bij ander building_id */}
      {warnDifferent && (
        <div className="mt-2 rounded-lg bg-[#FFF5EF] border-2 border-[#D4A373] p-3 space-y-2" data-testid={`automeasure-zone-warn-different-${zone.id}`}>
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-[#8B4A2E] mt-0.5 shrink-0" />
            <div>
              <div className="text-[13px] font-semibold text-[#8B4A2E]">Ander gebouw gedetecteerd</div>
              <div className="text-[12px] text-[#4F4F4D] mt-1">
                De nieuwe meting geeft een ander gebouw (ID <span className="font-mono">{warnDifferent.newId}</span>) dan de eerder gemeten meting (ID <span className="font-mono">{warnDifferent.oldId}</span>). Weet je zeker dat je wil vervangen?
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={confirmDifferentBuilding} className="bg-[#B84B45] hover:bg-[#9F3F3A] text-white text-xs h-9" data-testid={`automeasure-zone-warn-confirm-${zone.id}`}>Ja, vervang gebouw</Button>
            <Button onClick={() => setWarnDifferent(null)} variant="ghost" className="text-xs h-9" data-testid={`automeasure-zone-warn-cancel-${zone.id}`}>Annuleren</Button>
          </div>
        </div>
      )}

      {addressDialogOpen && (
        <AddressChangeDialog
          initial={addressDraft}
          onCancel={() => setAddressDialogOpen(false)}
          onSubmit={submitAddressChange}
        />
      )}
    </div>
  );
}

// -------------------------------------------------------------------
// Kleine adres-wijzig dialog (punt 2b).
// -------------------------------------------------------------------
function AddressChangeDialog({ initial, onCancel, onSubmit, onSubmitRemeasure }) {
  const [val, setVal] = useState(initial || "");
  return (
    <Dialog open={true} onOpenChange={(v) => { if (!v) onCancel(); }}>
      <DialogContent className="max-w-md" data-testid="automeasure-address-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-[#D96C4A]" /> Adres wijzigen
          </DialogTitle>
          <DialogDescription>Geef het te meten gebouw-adres in. Formaat: straat + nummer, postcode + gemeente.</DialogDescription>
        </DialogHeader>
        <Input
          value={val}
          onChange={(e) => setVal(e.target.value)}
          placeholder="bv. Mollenveldwijk 11, 3271 Scherpenheuvel-Zichem"
          data-testid="automeasure-address-dialog-input"
          autoFocus
        />
        <DialogFooter>
          <Button variant="ghost" onClick={onCancel} data-testid="automeasure-address-dialog-cancel">Annuleer</Button>
          <Button
            onClick={() => (onSubmitRemeasure ? onSubmitRemeasure(val.trim()) : onSubmit(val.trim()))}
            disabled={!val.trim()}
            className="bg-[#D96C4A] hover:bg-[#C05A3C] text-white"
            data-testid="automeasure-address-dialog-submit"
          >
            Bevestigen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
